<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import type { BoxSpace } from '@uiKit/elements/Box.vue'

export type CardVariant = 'elevated' | 'outlined' | 'flat'

interface Props {
  /** Semantic HTML tag. Default `'div'`. Use `is="article"` / `"section"` for content cards. */
  is?: string
  /**
   * Card chrome.
   * - `elevated` (default) — white bg, small shadow, no border
   * - `outlined` — white bg, 1px border, no shadow
   * - `flat` — white bg, no border, no shadow
   */
  variant?: CardVariant
  /** Internal padding. Default `'4'` (1rem). Token or raw CSS length. */
  padding?: BoxSpace | (string & {})
}

withDefaults(defineProps<Props>(), {
  is: 'div',
  variant: 'elevated',
  padding: '4',
})

defineOptions({ inheritAttrs: false })
</script>

<template>
  <Box v-bind="$attrs" :is="is" :padding="padding" :class="['card', `card--${variant}`]">
    <slot />
  </Box>
</template>

<style scoped>
/* Premium surface — subtle border, soft ambient shadow, larger radius.
   Reads as institutional Linear/Ramp/Stripe-Dashboard depth rather than
   heavy SaaS drop-shadow. */
.card {
  background: var(--p-surface-0);
  border-radius: 24px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-sizing: border-box;
  overflow: hidden;
}

.card--elevated {
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
}

.card--outlined {
  /* border already set on .card; no shadow */
  box-shadow: none;
}

.card--flat {
  border: none;
  box-shadow: none;
}
</style>
